/**
 * The support's arrow, on the client's page (taken from LogiFlow).
 *
 * The support points at an element, not at a point of the screen: we get the
 * element's rrweb id and an offset inside it (0 to 1). We find the element,
 * scroll it into view if needed, and draw the arrow with a pulsing outline.
 * The position follows the element (scroll, resize), so it stays right even
 * if both windows differ in size. One arrow at a time.
 */

interface Mirror {
  getNode(id: number): Node | null
}

const COLOR = '#2563eb'
let container: HTMLDivElement | null = null
let ring: HTMLDivElement | null = null
let tip: HTMLDivElement | null = null
let target: Element | null = null
let offset = { dx: 0.5, dy: 0.5 }
let loop: number | null = null
let last = ''

function styleOnce() {
  if (document.getElementById('screen-share-style')) return
  const style = document.createElement('style')
  style.id = 'screen-share-style'
  style.textContent = `@keyframes screen-share-pulse {
  0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, .45); }
  70% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
  100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
}`
  document.head.appendChild(style)
}

function create() {
  if (container) return
  styleOnce()
  container = document.createElement('div')
  container.setAttribute('aria-hidden', 'true')
  container.setAttribute('data-support-arrow', '')
  Object.assign(container.style, { position: 'fixed', inset: '0', pointerEvents: 'none', zIndex: '2147483646' })

  ring = document.createElement('div')
  Object.assign(ring.style, {
    position: 'absolute', border: `2px solid ${COLOR}`, borderRadius: '6px',
    animation: 'screen-share-pulse 1.4s ease-out infinite',
  })

  tip = document.createElement('div')
  Object.assign(tip.style, { position: 'absolute', width: '30px', height: '30px' })
  // A pointer arrow: its tip at the top left, on the spot shown.
  tip.innerHTML = `<svg width="30" height="30" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,.35))">
<path d="M3 2l7.5 19 2.6-7.9L21 10.5z" fill="${COLOR}" stroke="#fff" stroke-width="1.6" stroke-linejoin="round"/></svg>`

  container.append(ring, tip)
  document.body.appendChild(container)
}

function offScreen(r: DOMRect) {
  return r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth
}

function place() {
  if (!target || !target.isConnected || !ring || !tip) {
    clearArrow()
    return
  }
  const r = target.getBoundingClientRect()
  const key = [r.left, r.top, r.width, r.height].map(Math.round).join(',')
  // Touch the page only when the element moved: each change is sent to the support.
  if (key !== last) {
    last = key
    Object.assign(ring.style, {
      left: `${r.left - 4}px`, top: `${r.top - 4}px`, width: `${r.width + 8}px`, height: `${r.height + 8}px`,
    })
    Object.assign(tip.style, {
      left: `${r.left + offset.dx * r.width - 3}px`, top: `${r.top + offset.dy * r.height - 2}px`,
    })
  }
  loop = requestAnimationFrame(place)
}

/** Shows element `id` (rrweb). Returns false if it is no longer on the page. */
export function showArrow(mirror: Mirror, id: number, dx: number, dy: number): boolean {
  let node = mirror.getNode(id)
  if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement
  if (!(node instanceof Element)) return false
  clearArrow()
  target = node
  offset = { dx, dy }
  create()
  if (offScreen(target.getBoundingClientRect())) {
    target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
  }
  place()
  return true
}

export function clearArrow() {
  if (loop !== null) cancelAnimationFrame(loop)
  loop = null
  container?.remove()
  container = null
  ring = null
  tip = null
  target = null
  last = ''
}
